import { ReactElement } from 'react';
import { Page } from '@client/app/components/App/Page';
import { Address } from '@client/common/components/Address';
import { ExternalLink } from '@client/common/components/ExternalLink';
import { MoreLink } from '@client/common/components/MoreLink';
import { stringify } from '@client/common/utils/queryParams';
import { useTranslate } from '@client/i18n/hooks';
import { QUERY } from '@client/manufacturer/components/ListPage';
import { ROUTES } from '@client/routes/components/Router/routes';
import { useUrlCreator } from '@client/routes/hooks';
import { getBrandUrlParams } from '@client/routes/utils/url';
import { CardBox } from '@lib/components/CardBox';
import { DATA_PAIR_BREAKPOINT, DATA_PAIR_DIRECTION, DataPair } from '@lib/components/DataPair';
import { CommentType, ManufacturerAddressType } from 'b2b-common/core/manufacturer';
import { Props } from './ViewPage.types';
import './ViewPage.scss';

export const ViewPage = ({
  manufacturer: { data: manufacturer, isLoaded: isManufacturerLoaded },
  metaTags,
}: Props): ReactElement | null => {
  const t = useTranslate();
  const urlCreator = useUrlCreator();

  if (!isManufacturerLoaded || !manufacturer) {
    return null;
  }

  const renderComments = (comments: CommentType[]): ReactElement => (
    <>
      {comments.map((comment: CommentType): ReactElement => (
          <div key={comment.lineNumber}>{comment.comment}</div>
      ))}
    </>
  );

  const renderAddress = (addressObject: ManufacturerAddressType): ReactElement => {
    const { city, name, address, address2 } = addressObject;

    if (city || name || address || address2) {
      return <Address {...addressObject} street={address} streetAddition={address2} />;
    }

    return <>--</>;
  };

  return (
    <Page
      className="ManufacturerViewPage"
      title={manufacturer.name}
      metaTags={metaTags}
    >
      <h1 className='PageTitle'>{t('Manufacturer page')}</h1>

      <div className="row ManufacturerViewPage-Section">
        <div className="col-md-8">
          <CardBox className="ManufacturerViewPage-GeneralInformation">
            {manufacturer.linkLogo && (
              <div className="ManufacturerViewPage-LogoContainer">
                <img
                  alt={t('logo')}
                  className='ManufacturerViewPage-Logo'
                  src={manufacturer.linkLogo}
                />
              </div>
            )}

            <div>
              <DataPair
                breakpoint={DATA_PAIR_BREAKPOINT.xs}
                direction={DATA_PAIR_DIRECTION.row}
                label={t('Manufacturer')}
              >
                <span className="ManufacturerViewPage-TitleTile">
                  {manufacturer.name || '--'}
                </span>
              </DataPair>

              <DataPair
                breakpoint={DATA_PAIR_BREAKPOINT.xs}
                direction={DATA_PAIR_DIRECTION.row}
                label={t('Product category')}
              >
                <span className="ManufacturerViewPage-TitleTile">
                  {manufacturer.category || '--'}
                </span>
              </DataPair>

              <br />

              <MoreLink
                label={t('See all {manufacturerName} products', {
                  manufacturerName: manufacturer && manufacturer.name,
                })}
                url={`${urlCreator(ROUTES.brand)}${getBrandUrlParams(
                  manufacturer.name,
                )}`}
              />

              <MoreLink
                label={t(
                  'See {manufacturerName} support pages for other categories',
                  { manufacturerName: manufacturer && manufacturer.name },
                )}
                url={`${urlCreator(ROUTES.manufacturers)}?${stringify({
                  [QUERY]: manufacturer.name,
                })}`}
              />

              <MoreLink
                label={t('See all manufacturer support pages')}
                url={urlCreator(ROUTES.manufacturers)}
              />
            </div>
          </CardBox>
        </div>

        <div className="col-md-2">
          <CardBox
            className='ManufacturerViewPage-WarrantyTitle'
            title={t('Warranty period')}
          >
            <div className='ManufacturerViewPage-WarrantyPeriod'>
              <div className='ManufacturerViewPage-NumberValue'>
                {manufacturer.warranty.months}
              </div>

              <div className='ManufacturerViewPage-NumberUnit'>
                {t('months')}
              </div>
            </div>
          </CardBox>
        </div>

        <div className="col-md-2">
          <CardBox
            className='ManufacturerViewPage-WarrantyTitle'
            title={t('Warranty type')}
          >
            <div className='ManufacturerViewPage-WarrantyType'>
              {manufacturer.warranty.type || '--'}
            </div>
          </CardBox>
        </div>
      </div>

      <CardBox
        className='ManufacturerViewPage-Section'
        title={t('Product support')}
      >
        <div className='row'>
          <div className='col-md-12'>
            <DataPair
              breakpoint={DATA_PAIR_BREAKPOINT.xs}
              direction={DATA_PAIR_DIRECTION.row}
              label={t('Phone')}
            >
              {manufacturer.support.phone || '--'}
            </DataPair>

            <DataPair
              breakpoint={DATA_PAIR_BREAKPOINT.xs}
              direction={DATA_PAIR_DIRECTION.row}
              label={t('E-mail address')}
            >
              {manufacturer.support.email || '--'}
            </DataPair>

            <DataPair
              breakpoint={DATA_PAIR_BREAKPOINT.xs}
              direction={DATA_PAIR_DIRECTION.row}
              label={t('Support')}
            >
              <ExternalLink
                label={t('Support page')}
                url={manufacturer.support.weblink}
              />
            </DataPair>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12 ManufacturerViewPage-SupportInfo">
            {manufacturer.comments.support.length > 0
              && renderComments(manufacturer.comments.support)}
            {t('If you have any uncertainties or questions, please contact our call centre.')}
          </div>
        </div>
      </CardBox>

      <CardBox
        className='ManufacturerViewPage-Section'
        title={t('Repair information')}
      >
        <div className='row'>
          <div className='col-md-12'>
            {manufacturer.warranty.reparation.link && (
              <DataPair
                breakpoint={DATA_PAIR_BREAKPOINT.xs}
                direction={DATA_PAIR_DIRECTION.row}
                label={t('Warranty page')}
              >
                <ExternalLink
                  url={manufacturer.warranty.reparation.link}
                  label={t('Warranty information')}
                />
              </DataPair>
            )}

            <DataPair
              breakpoint={DATA_PAIR_BREAKPOINT.xs}
              direction={DATA_PAIR_DIRECTION.row}
              label={t('Repair service center')}
            >
              {renderAddress(manufacturer.warranty.reparation.serviceCenter)}
            </DataPair>

            {manufacturer.warranty.reparation.serviceCenter.homepage && (
              <DataPair
                breakpoint={DATA_PAIR_BREAKPOINT.xs}
                direction={DATA_PAIR_DIRECTION.row}
                label={t('Service page')}
              >
                <ExternalLink
                  url={manufacturer.warranty.reparation.serviceCenter.homepage}
                />
              </DataPair>
            )}
          </div>
        </div>

        {manufacturer.comments.repair.length > 0 && (
          <div className="row">
            <div className="col-md-12 ManufacturerViewPage-WarrantyInfo">
              {renderComments(manufacturer.comments.repair)}
            </div>
          </div>
        )}
      </CardBox>

      <CardBox
        className='ManufacturerViewPage-Section'
        title={t('DOA information')}
      >
        <div className='row'>
          <div className='col-md-8'>
            <DataPair
              breakpoint={DATA_PAIR_BREAKPOINT.xs}
              className='ManufacturerViewPage-DoaTermsLabel'
              direction={DATA_PAIR_DIRECTION.row}
              label={t('DOA terms')}
            >
              {manufacturer.doa.limitDays > 0 ? (
                <CardBox>
                  <div className='ManufacturerViewPage-DoaTerms'>
                    <div className='ManufacturerViewPage-DoaTermsDays'>
                      <div className='ManufacturerViewPage-NumberValue'>
                        {manufacturer.doa.limitDays}
                      </div>

                      <div className='ManufacturerViewPage-NumberUnit'>
                        {t('days')}
                      </div>
                    </div>

                    {t('DOA = DeadonArrival (Defect upon delivery)')}

                    <br />
                    <br />

                    {t(
                      'This manufacturer offers a special repair procedure for {days} days after the purchase.',
                      { days: manufacturer && manufacturer.doa.limitDays },
                      'DOA INFO',
                    )}
                  </div>
                </CardBox>
              ) : (
                '--'
              )}
            </DataPair>

            <DataPair
              breakpoint={DATA_PAIR_BREAKPOINT.xs}
              direction={DATA_PAIR_DIRECTION.row}
              label={t('DOA service center')}
            >
              {renderAddress(manufacturer.doa.doaCenter)}
            </DataPair>

            {manufacturer.doa.doaCenter.homepage && (
              <DataPair
                breakpoint={DATA_PAIR_BREAKPOINT.xs}
                direction={DATA_PAIR_DIRECTION.row}
                label={t('DOA service page')}
              >
                <ExternalLink url={manufacturer.doa.doaCenter.homepage} />
              </DataPair>
            )}

            <DataPair
              breakpoint={DATA_PAIR_BREAKPOINT.xs}
              direction={DATA_PAIR_DIRECTION.row}
              label={t('DOA page')}
            >
              {manufacturer.doa.link ? (
                <ExternalLink url={manufacturer.doa.link} />
              ) : (
                '--'
              )}
            </DataPair>
          </div>
        </div>

        {manufacturer.comments.doa.length > 0 && (
          <div className="row">
            <div className="col-md-12">
              {renderComments(manufacturer.comments.doa)}
            </div>
          </div>
        )}
      </CardBox>
    </Page>
  );
};
